/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import { css, keyframes } from "@emotion/react";
import { withMyTheme } from "../../theme/theme";
import { Typography, alpha, Tabs, Tab, Box, Tooltip, IconButton } from "@mui/material";
import { useTranslation } from "react-i18next";
import { mobileCss } from "../../util/isMobile";
import { useIsMobile } from "../../util/useIsMobile";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoIcon from '@mui/icons-material/Info';
import { ImageResource } from "../../resources/ImageResource";
import { MyButton } from "../../components/button/MyButton";
import { CONTACT_ID } from "../contact/Contact";
import { scrollToSection } from "../../util/scroll";
import SwipeableViews from 'react-swipeable-views';

export const PRICING_ID = "pricing";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const SectionContainerStyle = withMyTheme((theme) => css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 5rem 2rem;
  background-color: ${theme.palette.background.default};
  position: relative;
  overflow: hidden;
  
  ${mobileCss(`
    padding: 5rem 1rem;
  `)}
`);

const TitleStyle = withMyTheme((theme) => css`
  font-family: ${theme.typography.h1.fontFamily};
  font-size: 3.5rem;
  color: ${theme.palette.secondary.main};
  margin-bottom: 3rem;
  text-align: center;
  
  ${mobileCss(`
    font-size: 2.8rem;
    margin-bottom: 2rem;
  `)}
`);

const PackagesContainerStyle = withMyTheme(() => css`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 3rem;
  width: 100%;
  max-width: 1200px;
  margin-top: 6vh;
  
  ${mobileCss(`
    flex-direction: column;
    align-items: center;
    gap: 0;
    width: 100vw;
    margin-top: 0;
    overflow: hidden;
  `)}
`);

const PackageCardStyle = withMyTheme((theme) => css`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 450px;
  background-color: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  animation: ${fadeIn} 0.8s ease-out;
  position: relative;
  z-index: 1;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
  }
  
  &.featured {
    border: 2px solid ${theme.palette.secondary.main};
    transform: scale(1.05);
    
    &:hover {
      transform: translateY(-10px) scale(1.05);
    }
  }

  ${mobileCss(`
    width: 90vw;
    margin: 0 auto;
    transform: none !important;
    margin-top: 5vh;
    &.featured {
      transform: none !important;
      border: 2px solid ${theme.palette.secondary.main};
      &:hover {
        transform: none !important;
      }
    }
  `)}
`);

const IconCircleStyle = withMyTheme((theme) => css`
  position: absolute;
  top: 0;
  left: 50%;
  width: 14vw;
  height: 14vw;
  max-width: 18vh;
  max-height: 18vh;
  min-width: 120px;
  min-height: 120px;
  background-color: white;
  border-radius: 50%;
  transform: translate(-50%, -85%);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  border: 3px solid ${alpha(theme.palette.secondary.main, 0.5)};

  &.premium {
    border: 3px solid ${alpha(theme.palette.primary.main, 0.5)};
  }
  
  ${mobileCss(`
    width: 16vw;
    height: 16vw;
    min-width: 100px;
    min-height: 100px;
    transform: translate(-50%, -50%);
    top: 10px;
  `)}
`);

const PackageIconStyle = withMyTheme(() => css`
  width: calc(100% - 4vw);
  height: calc(100% - 4vw);
  opacity: 1;
  z-index: 3;
  pointer-events: none; /* Ensures the image doesn't interfere with clicking */
  object-fit: contain;
`);

const MobileTabsContainerStyle = withMyTheme((theme) => css`
  display: none;
  ${mobileCss(`
    display: flex;
    width: 100%;
    justify-content: center;
    margin-bottom: 1rem;
  `)}
`);

const MobileTabStyle = withMyTheme((theme) => css`
  min-width: 0;
  padding: 0.5rem;
  opacity: 0.7;
  transition: all 0.3s ease;
  
  &.Mui-selected {
    opacity: 1;
  }
`);

const TabIconStyle = withMyTheme(() => css`
  width: 40px;
  height: 40px;
  object-fit: contain;
  opacity: 0.8;
`);

const SwipeableViewsContainerStyle = withMyTheme(() => css`
  display: none;
  ${mobileCss(`
    display: block;
    width: 100vw;
    overflow-x: hidden;
    padding-top: 2vh;
  `)}
`);

const PackageHeaderStyle = withMyTheme((theme) => css`
  padding: 2rem;
  background-color: ${theme.palette.secondary.main};
  color: white;
  text-align: center;
  position: relative;
  z-index: 1;
  border-top-left-radius: 14px;
  border-top-right-radius: 14px;
  
  &.premium {
    background-color: ${theme.palette.primary.main};
    border-radius: 4px solid ${theme.palette.primary.main};
  }

  ${mobileCss(`
    padding-top: 3.5rem;
  `)}
`);

const PackageNameStyle = withMyTheme(() => css`
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`);

const PackagePriceContainerStyle = withMyTheme(() => css`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 1rem 0;
`);

const OldPriceStyle = withMyTheme(() => css`
  font-size: 1.2rem;
  text-decoration: line-through;
  opacity: 0.7;
  margin-bottom: 0.5rem;
`);

const PriceStyle = withMyTheme(() => css`
  font-size: 2.5rem;
  font-weight: bold;
`);

const PackageContentStyle = withMyTheme(() => css`
  padding: 2rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
  overflow: visible;
`);

const PackageSubtitleStyle = withMyTheme((theme) => css`
  font-size: 1.8rem;
  color: ${theme.palette.secondary.main};
  margin-bottom: 1rem;
  text-align: center;
  font-weight: 500;
  
  &.premium {
    color: ${theme.palette.primary.main};
  }
`);

const PackageDescriptionStyle = withMyTheme(() => css`
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
  text-align: center;
`);

const FeaturesListStyle = withMyTheme(() => css`
  margin-top: auto;
`);

const FeatureItemStyle = withMyTheme((theme) => css`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  font-family: ${theme.typography.body1.fontFamily};
  
  svg {
    color: ${theme.palette.secondary.main};
    margin-right: 0.8rem;
    flex-shrink: 0;
    margin-top: 3px;
  }
`);

const InfoIconStyle = withMyTheme((theme) => css`
  font-size: 1rem;
  color: ${theme.palette.secondary.main};
  margin-left: 0.5rem;
  cursor: pointer;
  vertical-align: middle;
  
  .MuiSvgIcon-root {
    display: flex;
    margin: 0 auto;
  }
`);

const TooltipStyle = withMyTheme((theme) => css`
  max-width: 300px;
  font-family: ${theme.typography.body1.fontFamily};
  font-size: 0.9rem;
  line-height: 1.5;
  padding: 0.75rem;
`);

const ButtonContainerStyle = withMyTheme(() => css`
  padding: 0 2rem 2rem;
  display: flex;
  justify-content: center;
  position: relative;
  z-index: 1;
`);

const ActionButtonStyle = withMyTheme((theme) => css`
  padding: 0.8rem 2rem;
  font-size: 1.1rem;
  border-radius: 30px;
  text-transform: none;
  transition: all 0.3s ease;
  font-weight: 500;
  
  &.basic {
    background-color: ${theme.palette.secondary.main};
    color: white;
    
    &:hover {
      background-color: ${theme.palette.primary.dark};
      animation: ${pulse} 0.8s infinite;
    }
  }
  
  &.premium {
    background-color: ${theme.palette.primary.main};
    color: white;
    
    &:hover {
      background-color: ${theme.palette.secondary.dark};
      animation: ${pulse} 0.8s infinite;
    }
  }
`);

interface PricingCardProps {
  packageType: 'basic' | 'premium';
  iconSrc: string;
  iconAlt: string;
  isFeatured?: boolean;
}

const PricingCard = ({ packageType, iconSrc, iconAlt, isFeatured = false }: PricingCardProps) => {
  const { t } = useTranslation();
  const isPremium = packageType === 'premium';
  const className = isPremium ? 'premium' : 'basic';
  
  return (
    <div css={PackageCardStyle} className={isFeatured ? 'featured' : ''}>
      <div css={IconCircleStyle} className={isFeatured ? `featured` : 'premium'}>
        <img 
          src={iconSrc}
          alt={iconAlt}
          css={PackageIconStyle}
        />
      </div>
      <div css={PackageHeaderStyle} className={isPremium ? 'premium' : ''}>
        <Typography css={PackageNameStyle}>
          {t(`pricing.packages.${packageType}.name`)}
        </Typography>
        <div css={PackagePriceContainerStyle}>
          {!isPremium && (
            <Typography css={OldPriceStyle}>
              {t(`pricing.packages.${packageType}.oldPrice`)}
            </Typography>
          )}
          <Typography css={PriceStyle}>
            {t(`pricing.packages.${packageType}.price`)}
          </Typography>
        </div>
      </div>
      
      <div css={PackageContentStyle}>
        <Typography css={PackageSubtitleStyle} className={isPremium ? 'premium' : ''}>
          {t(`pricing.packages.${packageType}.subtitle`)}
        </Typography>
        
        <Typography css={PackageDescriptionStyle}>
          {t(`pricing.packages.${packageType}.description`)}
        </Typography>
        
        {!isPremium && (
          <div css={FeaturesListStyle}>
            {(t(`pricing.packages.${packageType}.features`, { returnObjects: true }) as string[]).map((feature: string, index: number) => (
              <div key={index} css={FeatureItemStyle}>
                <CheckCircleOutlineIcon fontSize="small" />
                <span>
                  {feature}
                  {feature.includes('domain') && (
                    <Tooltip 
                      title={<Typography css={TooltipStyle}>{t('pricing.domainTooltip')}</Typography>}
                      arrow
                      placement="top"
                    >
                      <IconButton size="small" css={InfoIconStyle}>
                        <InfoIcon fontSize="inherit"/>
                      </IconButton>
                    </Tooltip>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      
      <div css={ButtonContainerStyle}>
        <MyButton text={t(`pricing.packages.${packageType}.button`)}
         additionalCss={ActionButtonStyle} className={className} 
         onClick={() => scrollToSection(CONTACT_ID)}
         variant={packageType === 'basic' ? 'secondary' : 'primary'}
         />
      </div>
    </div>
  );
};

export const Pricing = () => {
  const { t } = useTranslation();
  const isMobileView = useIsMobile();
  const [activeTab, setActiveTab] = useState(0);
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleSwipeChange = (index: number) => {
    setActiveTab(index);
  };

  const packages = [
    {
      packageType: "basic" as const,
      iconSrc: ImageResource.uncleOnWedding,
      iconAlt: "Uncle on Wedding",
      isFeatured: true
    },
    {
      packageType: "premium" as const,
      iconSrc: ImageResource.rockingAunt,
      iconAlt: "Rocking Aunt",
      isFeatured: false
    }
  ];
  
  return (
    <div css={SectionContainerStyle} id={PRICING_ID}>
      <Typography variant="h2" css={TitleStyle}>
        {t('pricing.title')}
      </Typography>
      
      {isMobileView ? (
        <>
          <Box css={MobileTabsContainerStyle}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              indicatorColor="secondary"
              textColor="secondary"
              aria-label="pricing packages tabs"
            >
              {packages.map((pkg, index) => (
                <Tab
                  key={pkg.packageType}
                  css={MobileTabStyle}
                  icon={
                    <img
                      src={pkg.iconSrc}
                      alt={pkg.iconAlt}
                      css={TabIconStyle}
                    />
                  }
                  aria-label={t(`pricing.packages.${pkg.packageType}.name`)}
                />
              ))}
            </Tabs>
          </Box>
          
          <div css={PackagesContainerStyle}>
            <SwipeableViews
              css={SwipeableViewsContainerStyle}
              index={activeTab}
              onChangeIndex={handleSwipeChange}
              enableMouseEvents
              resistance
            >
              {packages.map((pkg) => (
                <PricingCard
                  key={pkg.packageType}
                  packageType={pkg.packageType}
                  iconSrc={pkg.iconSrc}
                  iconAlt={pkg.iconAlt}
                  isFeatured={pkg.isFeatured}
                />
              ))}
            </SwipeableViews>
          </div>
        </>
      ) : (
        <div css={PackagesContainerStyle}>
          {packages.map((pkg) => (
            <PricingCard
              key={pkg.packageType}
              packageType={pkg.packageType}
              iconSrc={pkg.iconSrc}
              iconAlt={pkg.iconAlt}
              isFeatured={pkg.isFeatured}
            />
          ))}
        </div>
      )}
    </div>
  );
};
